import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type PartSelectionMode = 'best_effort' | 'human_in_the_loop';

/** Chat default preserves today's behavior: clarify ambiguous parts. */
const DEFAULT_MODE: PartSelectionMode = 'human_in_the_loop';

interface PartSelectionModeContextType {
  /**
   * How the search agent resolves ambiguity:
   *  - `human_in_the_loop`: pause and ask a clarifying question (default)
   *  - `best_effort`: auto-pick the most likely variant, never pauses
   */
  partSelectionMode: PartSelectionMode;
  setPartSelectionMode: (value: PartSelectionMode) => void;
  togglePartSelectionMode: () => void;
}

const PartSelectionModeContext = createContext<PartSelectionModeContextType | undefined>(undefined);

/**
 * Admin-facing "part selection mode" preference, held in session (in-memory)
 * state only — it is NOT persisted to localStorage. The persisted source of truth
 * for an active chat is the order's own `part_selection_mode` (admin-set on the
 * order); this context value is only used pre-order to seed the first search and
 * resets when the browser session ends. The chat-header Ask/Auto switch was
 * removed 2026-10-15, so this stays at DEFAULT_MODE unless set programmatically.
 */
export const usePartSelectionMode = (): PartSelectionModeContextType => {
  const context = useContext(PartSelectionModeContext);
  if (context === undefined) {
    throw new Error('usePartSelectionMode must be used within a PartSelectionModeProvider');
  }
  return context;
};

interface PartSelectionModeProviderProps {
  children: ReactNode;
}

export const PartSelectionModeProvider: React.FC<PartSelectionModeProviderProps> = ({ children }) => {
  // Session (in-memory) state only — intentionally NOT persisted. Resets to the
  // default when the browser session ends so it can never go stale against the
  // per-order mode that the backend treats as authoritative.
  const [partSelectionMode, setModeState] = useState<PartSelectionMode>(DEFAULT_MODE);

  const setPartSelectionMode = useCallback((value: PartSelectionMode) => setModeState(value), []);
  const togglePartSelectionMode = useCallback(
    () => setModeState(prev => (prev === 'human_in_the_loop' ? 'best_effort' : 'human_in_the_loop')),
    [],
  );

  return (
    <PartSelectionModeContext.Provider
      value={{ partSelectionMode, setPartSelectionMode, togglePartSelectionMode }}
    >
      {children}
    </PartSelectionModeContext.Provider>
  );
};

export default PartSelectionModeContext;
